import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ShieldCheck, Loader2, ArrowRight, Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Sparkles } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Card, CardContent } from "../../components/ui/Card"
import { Alert } from "../../components/ui/Alert"
import { ThemeToggle } from "../../components/ui/ThemeToggle"
import { api, ApiError } from "../../lib/api"
import { SCHOOL } from "../../lib/constants"
import { toast } from "sonner"

export default function SetupPage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [needsSetup, setNeedsSetup] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [form, setForm] = useState({
    email: "",
    fullName: "",
    displayName: "",
    password: "",
    confirmPassword: "",
    setupKey: "",
  })

  // Check if setup is needed
  useEffect(() => {
    api.get<{ needsSetup: boolean }>("/api/setup/status")
      .then(data => {
        setNeedsSetup(data.needsSetup)
        setChecking(false)
        if (!data.needsSetup) {
          // Setup already done — redirect to admin login
          setTimeout(() => navigate("/admin/login"), 2000)
        }
      })
      .catch(() => {
        setChecking(false)
        // Assume setup needed on error
      })
  }, [navigate])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match")
      return
    }
    setLoading(true)
    try {
      const res = await api.post<{ success: boolean; role: string }>("/api/setup", {
        email: form.email,
        password: form.password,
        fullName: form.fullName,
        displayName: form.displayName || undefined,
        setupKey: form.setupKey || undefined,
      })
      toast.success("Super admin account created! Redirecting to dashboard...")
      setTimeout(() => navigate("/admin"), 1500)
    } catch (e: any) {
      if (e instanceof ApiError) {
        toast.error(e.message)
        // If setup already done, redirect
        if (e.status === 403) {
          setTimeout(() => navigate("/admin/login"), 2000)
        }
      } else {
        toast.error("Setup failed")
      }
    } finally {
      setLoading(false)
    }
  }

  // Loading state — checking if setup is needed
  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink-950 p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold-400 mx-auto mb-3" />
          <p className="text-white/70 text-sm">Checking system status...</p>
        </div>
      </div>
    )
  }

  // Setup already done
  if (!needsSetup) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink-950 p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl bg-white dark:bg-ink-900">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gold-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-ink-900 dark:text-ink-100 mb-2">Setup Already Complete</h1>
            <p className="text-sm text-ink-600 dark:text-ink-400 mb-5">
              A super admin account already exists. Please login at the admin portal.
            </p>
            <Button onClick={() => navigate("/admin/login")} className="bg-brand-800 hover:bg-brand-900">
              Go to Admin Login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Setup form
  return (
    <div className="min-h-screen bg-ink-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pattern-dots opacity-10" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl" />

      {/* Top-right theme toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="container mx-auto px-4 py-12 relative min-h-screen flex items-center justify-center">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-500/20 mb-4 ring-4 ring-gold-500/10">
              <ShieldCheck className="w-9 h-9 text-gold-400" />
            </div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-300 mb-2">
              <Sparkles className="w-3 h-3" />
              Initial System Setup
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
              Create the First Super Admin
            </h1>
            <p className="text-white/70 max-w-xl mx-auto">
              This is a one-time operation to bootstrap the system. After this account is created,
              the setup page will become inaccessible and all future admin accounts must be created
              by the super admin through the dashboard.
            </p>
          </div>

          <Card className="border-0 shadow-2xl bg-white dark:bg-ink-900">
            <CardContent className="p-6 md:p-8">
              {/* Security notice */}
              <Alert variant="warning" className="mb-5">
                <div className="text-xs">
                  <strong className="block mb-1">Important — Read Carefully</strong>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Choose a strong password — you won't be able to recover it without email setup.</li>
                    <li>Use a valid email you have access to.</li>
                    <li>This account will have full system access.</li>
                    <li>This page will be locked after successful creation.</li>
                  </ul>
                </div>
              </Alert>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      required
                      placeholder="e.g. Ibrahim Sani"
                      value={form.fullName}
                      onChange={e => setForm({ ...form, fullName: e.target.value })}
                      className="mt-1.5"
                      minLength={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="displayName">Display Name (optional)</Label>
                    <Input
                      id="displayName"
                      placeholder="e.g. Provost"
                      value={form.displayName}
                      onChange={e => setForm({ ...form, displayName: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="admin@ymscoe.edu.ng"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-[11px] text-ink-500 dark:text-ink-400 mt-1">
                    Use your official school email if possible.
                  </p>
                </div>

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPwd ? "text" : "password"}
                      required
                      minLength={8}
                      placeholder="At least 8 characters with upper, lower, number, special"
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password strength indicator */}
                  {form.password && (
                    <PasswordStrength password={form.password} />
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      required
                      placeholder="Re-enter password"
                      value={form.confirmPassword}
                      onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                      className={form.confirmPassword && form.confirmPassword !== form.password ? "border-red-500" : ""}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {form.confirmPassword && form.confirmPassword !== form.password && (
                    <p className="text-[11px] text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="setupKey">Setup Key (if configured)</Label>
                  <Input
                    id="setupKey"
                    type="password"
                    placeholder="Leave empty if not configured"
                    value={form.setupKey}
                    onChange={e => setForm({ ...form, setupKey: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-[11px] text-ink-500 dark:text-ink-400 mt-1">
                    Optional: only required if the <code>SETUP_KEY</code> secret was set during deployment.
                  </p>
                </div>

                <div className="pt-3 border-t border-ink-200 dark:border-ink-700 flex items-center gap-2 text-xs text-ink-500 dark:text-ink-400">
                  <Lock className="w-3.5 h-3.5" />
                  <span>
                    All setup attempts are logged. Rate-limited to 3 attempts per hour per IP.
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !form.email || !form.fullName || !form.password || form.password !== form.confirmPassword}
                  className="w-full bg-brand-800 hover:bg-brand-900 dark:bg-brand-700 dark:hover:bg-brand-800 h-12 text-base"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating super admin...</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-2" /> Create Super Admin & Continue</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="text-center text-xs text-white/40 mt-6">
            <p>{SCHOOL.fullName}</p>
            <p className="mt-1">© {new Date().getFullYear()} — All setup activity is logged for security.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Inline password strength meter */
function PasswordStrength({ password }: { password: string }) {
  const checks = [
    { label: "8+ characters", ok: password.length >= 8 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Lowercase", ok: /[a-z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Special char", ok: /[!@#$%^&*()_+\-={}\[\]|\\:;"'<>,.?/~`]/.test(password) },
  ]
  const score = checks.filter(c => c.ok).length
  const strength = score === 5 ? "STRONG" : score >= 4 ? "GOOD" : score >= 3 ? "FAIR" : "WEAK"
  const colors = { WEAK: "bg-red-500", FAIR: "bg-gold-500", GOOD: "bg-brand-500", STRONG: "bg-brand-600" }
  const textColors = { WEAK: "text-red-600 dark:text-red-400", FAIR: "text-gold-600 dark:text-gold-400", GOOD: "text-brand-600 dark:text-brand-400", STRONG: "text-brand-700 dark:text-brand-300" }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 h-1.5 bg-ink-200 dark:bg-ink-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${colors[strength as keyof typeof colors]}`}
            style={{ width: `${(score / 5) * 100}%` }}
          />
        </div>
        <span className={`text-[10px] font-semibold uppercase ${textColors[strength as keyof typeof textColors]}`}>
          {strength}
        </span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {checks.map(c => (
          <div key={c.label} className={`flex items-center gap-0.5 text-[9px] ${c.ok ? "text-brand-600 dark:text-brand-400" : "text-ink-400"}`}>
            {c.ok ? <CheckCircle2 className="w-2.5 h-2.5" /> : <span className="w-2.5 h-2.5 rounded-full border border-current inline-block" />}
            <span>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
